import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, MapPin, Bell, LogOut, Shield, ChevronRight, Trash2, Edit2, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileLayout } from "@/components/layouts/MobileLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import API from "@/utils/api";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, login } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        const [addressRes, notifRes] = await Promise.all([
          axios.get(`${API}/addresses`, { withCredentials: true }),
          axios.get(`${API}/notifications`, { withCredentials: true }),
        ]);
        setAddresses(addressRes.data);
        setNotifications(notifRes.data);
        setUnreadCount(notifRes.data.filter((n) => !n.read).length);
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
      toast.success("Sesión cerrada");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      // Forzar logout local aunque falle el servidor
      navigate("/", { replace: true });
      toast.success("Sesión cerrada");
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/addresses/${addressId}`, {
        withCredentials: true,
      });
      setAddresses(addresses.filter((a) => a.address_id !== addressId));
      toast.success("Dirección eliminada");
    } catch (error) {
      toast.error("Error al eliminar dirección");
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address.address_id);
    setEditForm({
      label: address.label,
      street: address.street,
      city: address.city,
      postal_code: address.postal_code
    });
  };

  const handleSaveEdit = async (addressId) => {
    if (!editForm.street || !editForm.postal_code) {
      toast.error("Completa todos los campos");
      return;
    }

    try {
      const response = await axios.put(
        `${API}/addresses/${addressId}`,
        editForm,
        { withCredentials: true }
      );

      setAddresses(addresses.map(a =>
        a.address_id === addressId ? response.data : a
      ));
      setEditingAddress(null);
      setEditForm({});
      toast.success("Dirección actualizada");
    } catch (error) {
      console.error("Error updating address:", error);
      toast.error("Error al actualizar dirección");
    }
  };

  const handleCancelEdit = () => {
    setEditingAddress(null);
    setEditForm({});
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("Notificaciones marcadas como leídas");
    } catch (error) {
      toast.error("Error al marcar notificaciones");
    }
  };

  if (!user) {
    return (
      <MobileLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <User className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="font-heading text-xl font-bold mb-2">
            Inicia sesión
          </h2>
          <p className="text-muted-foreground text-center mb-6">
            Accede a tu cuenta para ver tu perfil y gestionar tus datos
          </p>
          <Button
            className="rounded-full bg-primary text-black hover:opacity-90"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={login}
            data-testid="profile-login-btn"
          >
            Iniciar sesión con Google
          </Button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="px-4 py-6 space-y-6">
        {/* User Info */}
        <div className="flex items-center gap-4">
          <img
            src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`}
            alt={user.name}
            className="w-16 h-16 rounded-full"
          />
          <div>
            <h1 className="font-heading text-xl font-bold">{user.name}</h1>
            <p className="text-muted-foreground text-sm">{user.email}</p>
            <Badge variant="secondary" className="mt-1 capitalize">
              {user.role === "customer" ? "Cliente" : user.role === "admin" ? "Admin" : "Repartidor"}
            </Badge>
          </div>
        </div>

        {/* Quick Actions */}
        {(user.role === "admin" || user.role === "driver") && (
          <div className="bg-primary/10 rounded-2xl p-4">
            <p className="font-medium mb-3">Panel de gestión</p>
            <div className="flex gap-2">
              {user.role === "admin" && (
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-full"
                  onClick={() => navigate("/admin")}
                  data-testid="profile-admin-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              {user.role === "driver" && (
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-full"
                  onClick={() => navigate("/driver")}
                  data-testid="profile-driver-btn"
                >
                  Repartidor
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Addresses */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Mis direcciones
            </h2>
          </div>

          {addresses.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No tienes direcciones guardadas
            </p>
          ) : (
            <div className="space-y-2">
              {addresses.map((address) => (
                <div
                  key={address.address_id}
                  className="bg-white rounded-xl border border-gray-100 p-4"
                >
                  {editingAddress === address.address_id ? (
                    // Formulario de edición
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm text-gray-600">Etiqueta</Label>
                        <Input
                          value={editForm.label}
                          onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                          className="mt-1"
                          placeholder="Casa, Trabajo, etc."
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Dirección</Label>
                        <Input
                          value={editForm.street}
                          onChange={(e) => setEditForm({ ...editForm, street: e.target.value })}
                          className="mt-1"
                          placeholder="Calle, número, piso"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm text-gray-600">Código postal</Label>
                          <Input
                            value={editForm.postal_code}
                            onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                            className="mt-1"
                            placeholder="04001"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Ciudad</Label>
                          <Input
                            value={editForm.city}
                            onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            className="mt-1"
                            placeholder="Almería"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={handleCancelEdit}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-primary hover:opacity-90 text-black"
                          style={{ backgroundColor: 'var(--primary)' }}
                          onClick={() => handleSaveEdit(address.address_id)}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Vista normal
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{address.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {address.street}, {address.postal_code} {address.city}
                        </p>
                        {address.lat && address.lng && (
                          <p className="text-xs text-green-600 mt-1">
                            📍 GPS: {address.lat.toFixed(6)}, {address.lng.toFixed(6)}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-600"
                          onClick={() => handleEditAddress(address)}
                          data-testid={`edit-address-${address.address_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteAddress(address.address_id)}
                          data-testid={`delete-address-${address.address_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notifications */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Notificaciones
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {unreadCount}
                </Badge>
              )}
            </h2>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                data-testid="mark-all-read-btn"
              >
                Marcar leídas
              </Button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No tienes notificaciones
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {notifications.slice(0, 5).map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`bg-white rounded-xl border p-4 ${notif.read ? "border-gray-100" : "border-primary/30 bg-primary/5"
                    }`}
                >
                  <p className="font-medium text-sm">{notif.title}</p>
                  <p className="text-sm text-muted-foreground">{notif.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notif.created_at).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Logout */}
        <Button
          variant="outline"
          className="w-full rounded-full text-destructive border-destructive/30 hover:bg-destructive/10"
          onClick={handleLogout}
          data-testid="profile-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar sesión
        </Button>
      </div>
    </MobileLayout>
  );
}
